import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import axios from "../utils/api/axios";
import { useStoreDispatch } from "../utils/hooks/hooks";
import { setCredentials as setReduxCredentials } from "../utils/store/reducer/slices/userReducer";
import { setCredentials as setLocalStorageCredentials, UserRole } from "../utils/api/endpoints/user/credentials";

// Safe error code mapping for SSO errors (CWE-74 / CWE-451 Mitigation)
const SSO_ERROR_CODE_MAP: Record<string, string> = {
  idp_error: "Authentication failed at identity provider.",
  invalid_request: "Missing or invalid authorization parameters.",
  invalid_state: "SSO state token is invalid or expired.",
  connection_inactive: "This SSO connection is disabled.",
  discovery_failed: "Failed to connect to identity provider.",
  token_exchange_failed: "Failed to validate identity provider token response.",
  invalid_id_token: "Identity provider did not return valid identity claims.",
  email_unverified: "Your email address is not verified at your identity provider.",
  missing_email: "Could not retrieve email from your Identity Provider.",
  domain_mismatch: "Authenticated email domain does not match a verified domain for this organization.",
  callback_failed: "Failed to complete Single Sign-On authentication."
};

const ALLOWED_SSO_MESSAGES = new Set([
  "Single Sign-On authentication failed.",
  "Failed to complete Single Sign-On authentication.",
  "Invalid SSO response from server."
]);

const resolveSafeSSOError = (searchParams: URLSearchParams): string => {
  const code = searchParams.get("error_code") || searchParams.get("error");
  if (code && SSO_ERROR_CODE_MAP[code]) {
    return SSO_ERROR_CODE_MAP[code];
  }
  const rawMsg = searchParams.get("message");
  if (rawMsg && ALLOWED_SSO_MESSAGES.has(rawMsg)) {
    return rawMsg;
  }
  return "Single Sign-On authentication failed.";
};

const SSOCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useStoreDispatch();
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const error = searchParams.get("error");

    if (error) {
      const safeMessage = resolveSafeSSOError(searchParams);
      if (window.opener) {
        try {
          window.opener.postMessage(
            {
              type: "SSO_ERROR",
              message: safeMessage
            },
            window.location.origin
          );
        } catch (e) {
          console.error("Failed to send error to parent window:", e);
        }
        window.close();
        return;
      }
      toast.error(safeMessage);
      const existingToken = localStorage.getItem("accessToken");
      if (existingToken) {
        navigate("/dashboard/settings/sso", { replace: true });
      } else {
        navigate("/login", { replace: true });
      }
      return;
    }

    const code = searchParams.get("code");

    if (!code) {
      toast.error("Invalid SSO response from server.");
      navigate("/login", { replace: true });
      return;
    }

    const processExchange = async () => {
      try {
        const res = await axios.post("/v2/sso/exchange", { code });
        const { accessToken, refreshToken, userId, userRole, username, email } = res.data;

        if (accessToken && refreshToken && userId && userRole !== undefined) {
          if (window.opener) {
            try {
              window.opener.postMessage(
                {
                  type: "SSO_TEST_SUCCESS",
                  message: "SSO Test Login Verified! Connection status automatically set to Testing."
                },
                "*"
              );
            } catch (e) {
              console.error("Failed to post message to opener:", e);
            }
            window.close();
            return;
          }

          const credentials = {
            userId: parseInt(userId, 10),
            username: username || "",
            userRole: parseInt(userRole, 10) as UserRole,
            userEmail: email || "",
            accessToken,
            refreshToken
          };

          // Store in localStorage & Redux
          setLocalStorageCredentials(credentials);
          dispatch(setReduxCredentials(credentials));

          toast.success("Successfully signed in via Single Sign-On!");

          // Redirection by role
          let dashboardPath = "/dashboard";
          if (
            [UserRole.ADMIN, UserRole.ADMIN_MANAGER, UserRole.SUB_ADMIN].includes(
              credentials.userRole
            )
          ) {
            dashboardPath = "/admin/dashboard";
          }

          navigate(dashboardPath, { replace: true });
        } else {
          toast.error("Failed to authenticate SSO session.");
          navigate("/login", { replace: true });
        }
      } catch (err: any) {
        console.error("SSO code exchange error:", err);
        toast.error(err?.response?.data?.message || "Failed to complete SSO authentication.");
        navigate("/login", { replace: true });
      }
    };

    processExchange();
  }, [searchParams, navigate, dispatch]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 dark:bg-slate-900">
      <div className="p-8 text-center bg-white rounded-lg shadow-md dark:bg-slate-800">
        <div className="w-12 h-12 mx-auto mb-4 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          Completing Single Sign-On...
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Please wait while we verify your credentials.
        </p>
      </div>
    </div>
  );
};

export default SSOCallback;
